import { GetStaticProps } from 'next'
import { getInsiderPageProps } from '../helpers/getInsiderPageProps'
import { REVALIDATE_PAGE, ROUTE_INSIDER_MEMBERSHIP } from '@aether/configs'
import { getSiteConfiguration } from '@aether/services/sanity-service'
import {
  InsiderHero,
  MainCopy,
  Benefits,
  ImageCarousel,
  InsiderAccordion,
} from '@aether/insider-membership'
import { styled } from '@aether/styles'
import { NextSeo } from 'next-seo'
import { seoImageLoader } from '@aether/components'
import { getSellingPlanByProductId } from '../helpers/getSellingPlanByProductId'
import { InView } from 'react-intersection-observer'
import { useState } from 'react'

const Grid = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '$48',

  '@md': {
    gap: '$96',
  },

  '& > *:last-child': {
    marginBottom: '$48',

    '@md': {
      marginBottom: '$96',
    },
  },
})

const Container = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '$48',
  maxWidth: '$containerSizeM',
  margin: '0 auto',
  paddingInline: '$36',

  '@md': {
    gap: '80px',
  },
})

type InsiderBaseProps = Awaited<ReturnType<typeof getInsiderPageProps>>
interface InsiderPageProps extends InsiderBaseProps {
  sellingPlanId?: string
}

export default function InsiderMembershipPage({
  page,
  sellingPlanId,
}: InsiderPageProps) {
  const { hero, mainCopy, benefits, imageCarousel, faq, seo } = page
  const url = `${process.env.BASE_URL}${ROUTE_INSIDER_MEMBERSHIP}`
  const seoImage = seoImageLoader(seo?.ogImage, seo?.ogAltImage)
  const [isFooterVisible, setIsFooterVisible] = useState(false)

  return (
    <>
      {seo && (
        <NextSeo
          title={seo.metaTitle}
          description={seo.metaDescription}
          canonical={url}
          openGraph={{
            url,
            type: 'website',
            title: seo.ogTitle,
            description: seo.ogDescription,
            images: [...(seoImage ? [seoImage] : [])],
          }}
          twitter={{
            cardType: 'summary_large_image',
          }}
        />
      )}
      <Grid>
        <InsiderHero
          hero={hero}
          sellingPlanId={sellingPlanId}
          isFooterVisible={isFooterVisible}
        />
        <Container>
          <MainCopy content={mainCopy} />
          <Benefits benefits={benefits} />
        </Container>
        <ImageCarousel imageCarousel={imageCarousel} />
        <Container>
          <InsiderAccordion faq={faq} />
        </Container>
      </Grid>
      <InView
        onChange={(inView) => setIsFooterVisible(inView)}
        rootMargin="-100px"
      >
        <div style={{ height: '1px' }} />
      </InView>
    </>
  )
}

export const getStaticProps: GetStaticProps<InsiderPageProps> = async ({
  preview,
  locale,
}) => {
  try {
    const [pageProps, siteConfiguration] = await Promise.all([
      getInsiderPageProps({
        locale,
        preview,
      }),
      getSiteConfiguration(locale, preview),
    ])
    const sellingPlanId = await getSellingPlanByProductId({
      id: pageProps.page.hero.productLink.id,
      locale,
    })

    if (!pageProps) {
      return {
        notFound: true,
        revalidate: REVALIDATE_PAGE,
      }
    }

    return {
      props: {
        ...pageProps,
        sellingPlanId,
        siteConfiguration,
      },
      revalidate: REVALIDATE_PAGE,
    }
  } catch (error) {
    console.error('Error in Insider Membership page:', error)

    return {
      notFound: true,
      revalidate: REVALIDATE_PAGE,
    }
  }
}
